// src/Services/accountUpgradeService.js
import { claimWelcomeBonus } from "./welcomeBonusService";
import {
  GoogleAuthProvider,
  linkWithPopup,
  createUserWithEmailAndPassword,
  linkWithCredential,
  EmailAuthProvider
} from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import authService from "./authService";
import { authErrorMessage } from "./authErrors";

class AccountUpgradeService {
  constructor() {
    this.googleProvider = new GoogleAuthProvider();
  }

  // Upgrade anonymous account to Google account
  async upgradeWithGoogle() {
    try {
      // Read the already-established anonymous session synchronously (this
      // modal is only ever shown once that session exists) rather than
      // awaiting ensureAuthenticated() first. An `await` here before the
      // popup call breaks the synchronous click-to-popup chain, which makes
      // Safari and other strict browsers block the popup as not user-initiated.
      const currentUser = authService.auth.currentUser;
      // A visitor (no guest account yet): sign in with Google directly, which
      // creates the full account. Creating a guest first would need an await
      // before the popup, and Safari would block it.
      if (!currentUser) {
        return authService.signInWithGoogle();
      }

      if (!currentUser.isAnonymous) {
        throw new Error("User is already linked to a permanent account");
      }

      console.log("🔗 Linking anonymous account to Google...");

      // Link the anonymous account with Google
      const result = await linkWithPopup(currentUser, this.googleProvider);
      const linkedUser = result.user;

      console.log("✅ Successfully linked to Google:", linkedUser.email);

      // Update user document with permanent account info
      await this.updateUserDocumentAfterUpgrade(linkedUser, {
        email: linkedUser.email,
        ...(await authService.startingNameFields(doc(db, "users", linkedUser.uid), linkedUser.displayName)),
        photoURL: linkedUser.photoURL,
        provider: 'google',
        upgradedAt: new Date(),
        isAnonymous: false
      });

      claimWelcomeBonus("link");

      return {
        success: true,
        user: linkedUser,
        message: "Account successfully linked to Google!"
      };

    } catch (error) {
      console.error("❌ Google linking failed:", error);
      
      // Handle specific error cases
      if (error.code === 'auth/credential-already-in-use') {
        return {
          success: false,
          error: 'account_exists',
          message: "This Google account is already linked to another user. Please sign in with that account instead."
        };
      } else if (error.code === 'auth/popup-closed-by-user') {
        return {
          success: false,
          error: 'popup_closed',
          message: "Sign-in popup was closed. Please try again."
        };
      } else if (error.code === 'auth/cancelled-popup-request') {
        return {
          success: false,
          error: 'popup_cancelled',
          message: "Sign-in was interrupted. Please try again."
        };
      } else if (error.code === 'auth/popup-blocked') {
        return {
          success: false,
          error: 'popup_blocked',
          message: "Your browser blocked the sign-in popup. Please allow popups for this site and try again."
        };
      } else {
        return {
          success: false,
          error: 'unknown',
          message: authErrorMessage(error, "We couldn't connect your Google account")
        };
      }
    }
  }

  // Upgrade anonymous account to email/password account
  async upgradeWithEmail(email, password) {
    try {
      // Ensure user is authenticated anonymously first
      const currentUser = await authService.ensureAuthenticated();
      
      if (!currentUser.isAnonymous) {
        throw new Error("User is already linked to a permanent account");
      }

      console.log("🔗 Linking anonymous account to email:", email);

      // Create email credential
      const credential = EmailAuthProvider.credential(email, password);
      
      // Link the anonymous account with email/password
      const result = await linkWithCredential(currentUser, credential);
      const linkedUser = result.user;

      console.log("✅ Successfully linked to email:", linkedUser.email);

      // Update user document with permanent account info
      await this.updateUserDocumentAfterUpgrade(linkedUser, {
        email: linkedUser.email,
        provider: 'email',
        upgradedAt: new Date(),
        isAnonymous: false
      });

      claimWelcomeBonus("link");

      return {
        success: true,
        user: linkedUser,
        message: "Account successfully created with email!"
      };

    } catch (error) {
      console.error("❌ Email linking failed:", error);

      // Handle specific error cases
      if (error.code === 'auth/email-already-in-use') {
        return {
          success: false,
          error: 'email_exists',
          message: "This email is already registered. Please sign in with this email instead."
        };
      } else if (error.code === 'auth/invalid-email') {
        return {
          success: false,
          error: 'invalid_email',
          message: "Please enter a valid email address."
        };
      } else if (error.code === 'auth/weak-password') {
        return {
          success: false,
          error: 'weak_password',
          message: "Password should be at least 6 characters long."
        };
      } else {
        return {
          success: false,
          error: 'unknown',
          message: authErrorMessage(error, "We couldn't create your account")
        };
      }
    }
  }

  // Update user document after successful upgrade
  async updateUserDocumentAfterUpgrade(user, additionalData) {
    try {
      const userRef = doc(db, "users", user.uid);
      
      await updateDoc(userRef, {
        ...additionalData,
        updatedAt: new Date()
      });

      console.log("✅ User document updated after upgrade");
    } catch (error) {
      console.error("❌ Failed to update user document:", error);
      // Don't throw here - the linking succeeded, this is just metadata
    }
  }

  // Check if current user can be upgraded
  async canUpgrade() {
    try {
      await authService.getExistingUserId();
      // A visitor (no account yet) or a guest can create a full account.
      return !authService.currentUser || authService.currentUser.isAnonymous;
    } catch (error) {
      console.error("Error checking upgrade eligibility:", error);
      return false;
    }
  }

  // Get current user's account type
  async getAccountInfo() {
    try {
      await authService.getExistingUserId();
      const currentUser = authService.currentUser;

      // A visitor (no account yet) is shown the same options as a guest.
      if (!currentUser || currentUser.isAnonymous) {
        return {
          type: 'anonymous',
          canUpgrade: true,
          email: null,
          displayName: null
        };
      } else {
        const providerData = currentUser.providerData[0];
        return {
          type: 'permanent',
          canUpgrade: false,
          email: currentUser.email,
          displayName: currentUser.displayName,
          provider: providerData?.providerId || 'unknown'
        };
      }
    } catch (error) {
      console.error("Error getting account info:", error);
      return {
        type: 'unknown',
        canUpgrade: false,
        email: null,
        displayName: null
      };
    }
  }
}

// Export singleton instance
export const accountUpgradeService = new AccountUpgradeService();
export default accountUpgradeService;